




























































































import {Component, Prop, Vue, Emit, Watch} from 'nuxt-property-decorator'
import {Loading} from '../Loading'
import {Tag} from '../Tag'
import Checkbox from '../FormCheckboxInputs/FormCheckbox.vue'
import {debounce} from 'debounce'

@Component({
  components: {
    Loading,
    Tag,
    Checkbox,
  },
})
class FormMultiSelect extends Vue {
  name = 'form-multi-select'

  /**
   * Options for the dropdown
   */
  @Prop({type: Array, default: []})
  options!: any[]

  /**
   * Error message
   */
  @Prop({type: String, default: undefined})
  errorMessage!: string | undefined

  /**
   * Options for the dropdown
   */
  @Prop({type: Boolean, default: false})
  searchable!: boolean

  /**
   * Minimal style
   */
  @Prop({type: Boolean, default: false})
  minimal!: boolean

  /**
   * Do the options change async?
   */
  @Prop({type: Boolean, default: false})
  async!: boolean

  /**
   * v-model setup
   */
  @Prop({type: Array, required: false, default: []})
  value!: any

  /**
   * Are the options in a loading state?
   */
  @Prop({type: Boolean, default: false})
  loading!: boolean

  /**
   * Placeholder text
   */
  @Prop({type: String, default: 'Select an option'})
  placeholder!: string

  /**
   * Label text
   */
  @Prop({type: String, default: ''})
  label!: string

  /**
   * Which property are we using to display the current value in text form
   */
  @Prop({type: String, default: 'name'})
  displayProperty!: string

  /**
   * Which property are we using to detect the current value (unique ID)
   */
  @Prop({type: String, default: 'id'})
  valueProperty!: string

  @Watch('options')
  onOptionsChange(values) {
    this.cacheOptionValues(values)
  }

  cacheOptionValues(values) {
    values.forEach((value) => {
      this.cachedValues[value[this.valueProperty]] = value
    })
  }

  cachedValues = {}

  txt: null | string = null

  originalSearchText =
    this.value && this.value[this.displayProperty]
      ? this.value[this.displayProperty]
      : ''
  focused: boolean = false
  hoverIndex: number = 0
  closeTimeout?: NodeJS.Timeout

  get searchText() {
    return this.txt === null
      ? this.value && this.value[this.displayProperty]
        ? this.value[this.displayProperty]
        : ''
      : this.txt
  }

  set searchText(value: string | null) {
    this.txt = value
  }

  get currentOptions() {
    if (
      this.async === false &&
      this.searchable &&
      this.searchText != this.originalSearchText
    ) {
      return this.options.filter(
        (o) =>
          (o[this.displayProperty] || '')
            .toLowerCase()
            .indexOf((this.searchText || '').toLowerCase()) !== -1
      )
    }

    return this.options
  }

  get open() {
    return this.focused
  }

  get inputElement(): HTMLElement {
    return this.$refs.input as HTMLElement
  }

  /**
   * Value changed, updates v-model
   */
  @Emit('input')
  setInput(value) {
    return value
  }

  setOption(option) {
    if (!option) {
      return
    }

    if (this.value.indexOf(option[this.valueProperty]) !== -1) {
      return this.setInput(
        this.value.filter((v) => v !== option[this.valueProperty])
      )
    } else {
      return this.setInput([...this.value, option[this.valueProperty]])
    }
  }

  select(option = null, keepBlurred = false) {
    if (this.closeTimeout && !keepBlurred) {
      clearTimeout(this.closeTimeout)
      this.inputElement.focus()
    }

    this.setOption(option)
  }

  removeValue(value) {
    const option = this.options.find((op) => op[this.valueProperty] === value)

    if (option) {
      if (!option.disabled) this.select(option, true)
    } else {
      this.setInput(this.value.filter((v) => v !== value))
    }
  }

  onDelete() {
    if (!this.txt && this.value && this.value.length > 0) {
      this.removeValue(this.value[this.value.length - 1])
    }
  }

  selectHovered() {
    if (this.currentOptions.length > 0) {
      this.setOption(this.currentOptions[this.hoverIndex])
    }
  }

  @Emit('clear')
  clearText() {
    this.inputElement.focus()
    this.focused = true
    this.txt = ''

    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout)
    }
  }

  focus() {
    this.cacheOptionValues(this.options)
    this.inputElement.focus()
    this.focused = true
  }

  blur() {
    this.closeTimeout = setTimeout(() => {
      this.focused = false
      this.searchText = null
      this.originalSearchText = this.searchText
    }, 250)
  }

  moveUp() {
    if (--this.hoverIndex < 0) {
      this.hoverIndex = this.currentOptions.length - 1
    }
  }

  moveDown() {
    if (++this.hoverIndex >= this.currentOptions.length) {
      this.hoverIndex = 0
    }
  }

  searchChanged = debounce(function(this: FormMultiSelect) {
    /**
     * Search text input changed. You should update the options here
     */
    this.$emit('changed', this.searchText)
  }, 300)

  input() {
    this.hoverIndex = 0
    this.originalSearchText = null
    this.searchChanged()
  }
}

export default FormMultiSelect
